// SPDX-License-Identifier: AGPL-3.0-only
/**
 * Bootstrap query + types: the one round trip that primes the shell — session
 * user, resolved preference axes, the nav tree, and version/configVersion
 * stamps. Held under `['bootstrap']` with `staleTime: Infinity`; WS
 * `config-changed` invalidates it (app/ws.ts).
 *
 * SYNC NOTE: these shapes mirror the Zod reply schema in
 * `apps/server/src/routes/bootstrap/schema.ts` (the dashboard imports server
 * types type-only-or-copied per the matrix — until an
 * `@adminium/server/api-types` subpath ships, this is the copied mirror).
 * Change both together.
 */
// The narrow subpath, deliberately: the barrel makes the whole package
// statically reachable from this module, and this module is in the entry
// chunk. See the note in the package's `nav-groups.ts`.
import { BUILTIN_NAV_GROUP_KEYS } from '@adminium/add-on-contracts/nav-groups';
import { queryOptions } from '@tanstack/react-query';
import type { Accent, Density, Dir, ThemePref } from '@adminium/tokens';
import type { Locale } from '@adminium/ui';

import { api, ApiError, setCsrfToken } from './api.js';

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  status: 'active' | 'invited' | 'suspended';
  totpEnabled: boolean;
  lastLoginAt: number | null;
  createdAt: number;
  updatedAt: number;
}

/**
 * The five built-in sidebar groups, in rail order.
 *
 * RE-EXPORTED FROM `@adminium/add-on-contracts` (51b), which is where an add-on
 * manifest's `nav.group` is validated against them. One list: a key the
 * manifest accepts and the rail does not know is a page that installs and never
 * appears. Generated pages still live in these five and nowhere else; an
 * add-on's own group rides `addOnNav.groups`, not this type.
 */
export const NAV_GROUP_KEYS = BUILTIN_NAV_GROUP_KEYS;
export type NavGroupKey = (typeof NAV_GROUP_KEYS)[number];

/**
 * A rail row an add-on contributes, and a group it brought for one (51b).
 *
 * `labelKey` + `fallback` rather than a resolved string, the shape a generated
 * nav item already has: the server renders no add-on bundles, so it has the
 * manifest's English fallback and nothing else. Until the add-on's own
 * catalogue is merged into the client's i18n, `t()` returns that fallback.
 */
export interface AddOnNavPage {
  addOnKey: string;
  ref: string;
  labelKey: string;
  fallback: string;
  /** lucide icon name (kebab-case). */
  icon: string;
  /** The module path inside the add-on's package (`pages[].client`). */
  client: string;
  /** Built-in or add-on-declared; resolved server-side (`library` by default). */
  group: string;
  order: number;
  adminOnly: boolean;
  detail: boolean;
}

export interface AddOnNavGroup {
  key: string;
  labelKey: string;
  fallback: string;
  order: number;
  addOnKey: string;
}

export interface AddOnNav {
  groups: AddOnNavGroup[];
  pages: AddOnNavPage[];
}


export interface NavItem {
  pageId: string;
  slug: string;
  labelKey: string;
  fallback: string;
  /** lucide icon name (kebab-case). */
  icon: string;
  badge?: 'unread-count' | 'pending-count';
  order: number;
  /**
   * Owning connection: with 2+ connections the sidebar sub-labels items by
   * connection display name. Optional — older fixtures omit it.
   */
  connectionId?: string | null;
  connectionName?: string | null;
  /**
   * The owning connection's ISO-4217 currency. Optional for fixtures
   * predating it; the server always sends it (null when unset, which is what
   * keeps the money cells on their historical `USD` fallback).
   */
  currency?: string | null;
  /**
   * The page envelope's `source.table` — feeds {@link slugForTable} so
   * record pages can cross-link related rows to the page that shows their
   * table. Optional for fixtures predating it; the server always sends it
   * (null for source-less pages).
   */
  sourceTable?: string | null;
}

export interface NavTree {
  groups: Array<{ key: NavGroupKey; items: NavItem[] }>;
}

type PrefSource = 'system' | 'global' | 'user';

/** server-resolved axes + per-axis provenance. */
export interface ResolvedPrefs {
  theme: ThemePref;
  accent: Accent;
  density: Density;
  locale: Locale;
  dir: Dir;
  source: { theme: PrefSource; accent: PrefSource; density: PrefSource; locale: PrefSource; dir: PrefSource };
}

/**
 * One row of a blended app's sidebar section.
 *
 * `label` arrives already resolved to this session's locale — the build emits
 * all eight into `surface.json` and the server picks one, so nothing here has
 * to know about the app's own i18n.
 */
export interface HostedNavItem {
  id: string;
  /** Path under `/a/<appKey>/`, no leading slash. May be empty (the root). */
  path: string;
  label: string;
  /** lucide icon name; absent means the sidebar's neutral glyph. */
  icon?: string;
  /** A lens within the surface — its own row, not a permission. */
  persona?: string;
}

/** A hosted app blended into this dashboard's sidebar. */
export interface HostedApp {
  appKey: string;
  /** Instance slug when this section is an extra tenant; absent on the app's
   * own. */
  instance?: string;
  label: string;
  items: HostedNavItem[];
}

/** One built project file. */
export interface ProjectClientFile {
  url: string;
  /** Subresource-integrity value, `sha384-…`. */
  integrity: string;
}

export interface ProjectClientEntry {
  module: ProjectClientFile;
  /** Every chunk the module imports, directly or not. */
  imports: ProjectClientFile[];
  styles: ProjectClientFile[];
}

export interface ProjectClientPage extends ProjectClientEntry {
  slug: string;
}

export interface ProjectClientWidget extends ProjectClientEntry {
  /** `project.<file name>`. */
  id: string;
  kind: 'cell' | 'card';
  title: string | null;
}

export interface BootstrapProject {
  /** Database key from `adminium.config.ts` → connection id. */
  databases: Record<string, string>;
  /** Null where project code never loads (the desktop app). */
  client: {
    /** Changes with every build of the pages and widgets. */
    digest: string;
    pages: ProjectClientPage[];
    widgets: ProjectClientWidget[];
  } | null;
}

export interface BootstrapData {
  user: SessionUser;
  roles: string[];
  prefs: ResolvedPrefs;
  nav: NavTree;
  version: string;
  configVersion: number;
  llm: { enabled: boolean };
  /**
   * Whether this session may open the page assistant — the only thing a host
   * page knows before the modal's chunk loads, and therefore what decides
   * whether its button renders at all.
   *
   * Optional here only so fixtures predating the field keep typechecking; the
   * server always sends it (the field is required in the Zod reply schema).
   * Read through {@link assistantAllowed} rather than directly, so no call
   * site has to repeat the `?? false`.
   */
  assistant?: { allowed: boolean; name: string };
  /**
   * The session-bound CSRF token every mutating call echoes in
   * `x-adminium-csrf`. Optional here only so fixtures predating it keep
   * typechecking; the server always sends it (the field is required in the
   * Zod reply schema).
   */
  csrfToken?: string;
  /**
   * Hosted apps blended into the sidebar.
   *
   * Optional here only so fixtures predating the field keep typechecking; the
   * server always sends it, as `[]` on the overwhelming majority of instances.
   * Read through {@link hostedAppsOf} rather than directly, so no call site has
   * to repeat the `?? []`.
   */
  hostedApps?: HostedApp[];
  /**
   * Rail rows contributed by installed, enabled add-ons (51b). Optional for the
   * same reason as the fields around it — fixtures predating it — and read
   * through {@link addOnNavOf}.
   */
  addOnNav?: AddOnNav;
  /**
   * Pages hidden from the sidebar but alive (follow-up): Studio's "Hide from
   * sidebar" and the generated cascade-child default both land pages here.
   * Same shape as nav items, no group. Everything that RESOLVES pages — the
   * `/p/$slug` loader, topbar titles, record-page related tabs — reads {@link
   * findPageBySlug} / {@link slugForTable}, which consult this list too;
   * everything that LISTS pages (sidebar, palette Navigate, G-chords) stays
   * on the nav tree, which is the entire point of hiding. Optional only so
   * fixtures predating it keep typechecking; the server always sends it.
   */
  hiddenPages?: NavItem[];
  /**
   * Pages whose connection an operator PAUSED (meta wave 0019).
   *
   * Not a third flavour of `hiddenPages`, and the difference is the whole
   * reason it is its own field: a hidden page is still ENUMERABLE — related
   * tabs read its column specs, cross-links resolve its slug — while a paused
   * page must be enumerable by nothing, because the source behind it is off.
   *
   * So exactly one caller reads it: {@link findPageBySlug}, which answers a
   * URL somebody already has. A bookmark or a tab left open when the pause
   * landed resolves through here and renders the `connection-paused` state
   * instead of a 404 — the page has not gone, its database has. Nothing that
   * OFFERS a page (sidebar, palette, related tabs, {@link slugForTable}) may
   * consult it.
   */
  pausedPages?: NavItem[];
  /**
   * The project folder this server runs, absent on every other server. Read
   * through `project/client.ts`.
   */
  project?: BootstrapProject;
}

/** May this session open the page assistant? See the field's note. */
export function assistantAllowed(bootstrap: BootstrapData): boolean {
  return bootstrap.assistant?.allowed ?? false;
}

/**
 * What the assistant is called here. The fallback is the shipped default,
 * which is what a fixture predating the field describes anyway — and a button
 * labelled *Ask* with nothing after it is worse than one naming the default.
 */
export function assistantName(bootstrap: BootstrapData): string {
  const name = bootstrap.assistant?.name ?? '';
  return name === '' ? 'Milo' : name;
}

/** The blended apps, never undefined — see the field's note. */
export function hostedAppsOf(bootstrap: BootstrapData): HostedApp[] {
  return bootstrap.hostedApps ?? [];
}

/** The add-on rail rows and groups, never undefined — see the field's note. */
export function addOnNavOf(bootstrap: BootstrapData): AddOnNav {
  return bootstrap.addOnNav ?? { groups: [], pages: [] };
}

/** The hidden pages, never undefined — see the field's note. */
export function hiddenPagesOf(bootstrap: BootstrapData): NavItem[] {
  return bootstrap.hiddenPages ?? [];
}

/** The paused-connection pages, never undefined — see the field's note. */
export function pausedPagesOf(bootstrap: BootstrapData): NavItem[] {
  return bootstrap.pausedPages ?? [];
}

/** A blended app by key, or null — the `/a/$appKey` route's resolver. */
export function hostedAppByKey(bootstrap: BootstrapData, appKey: string): HostedApp | null {
  return hostedAppsOf(bootstrap).find((app) => app.appKey === appKey) ?? null;
}

/**
 * Which nav item a path is inside — longest match, the same rule the app's own
 * router uses, so the sidebar highlight and the framed screen never disagree.
 *
 * Lives HERE, not beside the frame that also uses it, for one measured reason:
 * `SidebarNav` is in the entry chunk and `AppSurfacePage` is lazy, so importing
 * this from there would drag the page, the frame and everything they import
 * back into the synchronous set — silently undoing the laziness the
 * entry-budget gate exists to enforce. A pure function over the bootstrap shape
 * belongs in the bootstrap module anyway.
 *
 * A path under NO item resolves to null. That is normal: the nav is what the
 * sidebar offers, not an allow-list of what the app may show, and a detail
 * screen the app does not list still renders.
 */
export function activeHostedItem(app: HostedApp, path: string): HostedNavItem | null {
  let best: HostedNavItem | null = null;
  for (const item of app.items) {
    if (item.path !== path && !path.startsWith(`${item.path}/`)) continue;
    if (best === null || item.path.length > best.path.length) best = item;
  }
  return best;
}

/** Never retry auth failures — they mean "go to /login", not "try harder". */
function retryBootstrap(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

export function bootstrapQuery() {
  return queryOptions({
    queryKey: ['bootstrap'] as const,
    staleTime: Infinity,
    retry: retryBootstrap,
    queryFn: async () => {
      const { data } = await api.get<{ data: BootstrapData }>('/api/v1/bootstrap');
      // The single writer of the CSRF holder every mutating call site reads
      // (app/api.ts). Here rather than in a component because the four call
      // sites that bypass `api` are not inside the React tree's data flow,
      // and because this query is the one thing guaranteed to run before the
      // shell can mutate anything.
      setCsrfToken(data.csrfToken ?? null);
      return data;
    },
  });
}

/** All nav items in group order — palette/Navigate, G-chords, 404 chips. */
export function flattenNav(nav: NavTree): NavItem[] {
  return nav.groups.flatMap((group) => group.items);
}

export function findNavItemBySlug(nav: NavTree, slug: string): NavItem | null {
  return flattenNav(nav).find((item) => item.slug === slug) ?? null;
}

/**
 * A page by slug — nav items first, then hidden pages. This is the RESOLUTION
 * helper (the `/p/$slug` loader, topbar titles, record bindings): a page
 * hidden from the sidebar still answers its URL, still titles its tab, and
 * still lends its column specs to a parent's related tab. Listing surfaces
 * keep using {@link findNavItemBySlug} — offering hidden pages in the rail or
 * the palette would un-hide them.
 *
 * Paused pages resolve here too, and LAST. Nothing offers them any more, so
 * the only way to arrive at one is to already hold its URL — a bookmark, or a
 * tab that was open when the pause landed. Refusing to resolve it would turn
 * "your admin switched this database off" into "no such page", which sends the
 * reader looking for a page that is sitting right where they left it.
 */
export function findPageBySlug(bootstrap: BootstrapData, slug: string): NavItem | null {
  return (
    findNavItemBySlug(bootstrap.nav, slug) ??
    hiddenPagesOf(bootstrap).find((item) => item.slug === slug) ??
    pausedPagesOf(bootstrap).find((item) => item.slug === slug) ??
    null
  );
}

/**
 * The slug of the page showing `table` on `connectionId`, or null: record
 * pages link related-tab rows to the page that shows their table; a table
 * with no page renders un-linked (honest degradation, no dead affordance).
 * Hidden pages count — a cascade-owned child's page IS its rows' record-page
 * home even though the sidebar does not list it. First match wins on the
 * (rare) duplicate — nav order is the user-facing precedence, and nav beats
 * hidden.
 */
export function slugForTable(
  bootstrap: BootstrapData,
  connectionId: string | null,
  table: string,
): string | null {
  // A linear scan, deliberately: the nav holds tens of items, callers fire on
  // clicks and tab activations, and this module is in the ENTRY set — map
  // machinery and a WeakMap cache cost real ratcheted bytes to save nothing
  // measurable (check-entry-budget).
  for (const item of [...flattenNav(bootstrap.nav), ...hiddenPagesOf(bootstrap)]) {
    if ((item.sourceTable ?? null) === table && (item.connectionId ?? null) === connectionId) {
      return item.slug;
    }
  }
  return null;
}

/**
 * `/` redirect target: the first Workspace nav item, else the first item
 * anywhere; `null` when the nav is empty (zero connections → the
 * `empty-no-sources` home state until `/welcome` lands in Wave B).
 */
export function defaultPageSlug(nav: NavTree): string | null {
  const workspace = nav.groups.find((group) => group.key === 'workspace');
  const first = workspace?.items[0] ?? flattenNav(nav)[0];
  return first?.slug ?? null;
}
